import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useCallback, useEffect, useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { CloseIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { ClubLogo } from "../../../../../../../components/ClubLogo/ClubLogo";
import { useUnlockAllSlots } from "../../../../../../../features/LockSlots";
import { BOOKING_SUMMARY_CONTENT_MAX_WIDTH } from "../../bookingSummaryWidth";
import { BookingHeaderFragment_BookingSummary$key } from "./__generated__/BookingHeaderFragment_BookingSummary.graphql";
import { BookingHeaderFragment_booking$key } from "./__generated__/BookingHeaderFragment_booking.graphql";

type Props = {
  clubRef: BookingHeaderFragment_BookingSummary$key | null;
  bookingRef: BookingHeaderFragment_booking$key | null;
  handleGoBack: () => void;
  isMultipleBooking?: boolean;
  title?: string;
};

export const BookingHeader = ({
  clubRef,
  bookingRef,
  handleGoBack,
  isMultipleBooking,
  title,
}: Props) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isLeaveButtonDisabled, setIsLeaveButtonDisabled] = useState(false);
  const club = useFragment(BookingHeaderFragmentString, clubRef);
  const booking = useFragment(BookingHeaderFragmentBookingString, bookingRef);
  const isDraft = booking?.status === "DRAFT";

  const [unlockAllSlots, isUnlockingAllSlots] = useUnlockAllSlots();

  useEffect(() => {
    if (!isUnlockingAllSlots) {
      setIsLeaveButtonDisabled(false);
    }
  }, [isUnlockingAllSlots]);

  const callback = useCallback(() => {
    setIsLeaveButtonDisabled(false);
    setConfirmationModalOpen(false);
    handleGoBack();
  }, [handleGoBack]);

  const handleUnlockSlot = useCallback(() => {
    setIsLeaveButtonDisabled(true);
    void unlockAllSlots(callback, () => setIsLeaveButtonDisabled(false));
  }, [unlockAllSlots, callback]);
  return (
    <>
      <HStack
        backgroundColor="grayscale-0"
        justifyContent="space-between"
        alignItems="center"
        maxWidth={BOOKING_SUMMARY_CONTENT_MAX_WIDTH}
        width="100%"
        padding={5}
        {...(isMultipleBooking && {
          marginLeft: "auto",
          marginRight: "auto",
        })}
      >
        <Button
          accessibilityLabel={t`Close button`}
          variant="secondary"
          onPress={() => {
            if (isDraft) {
              setIsLeaveButtonDisabled(false);
              setConfirmationModalOpen(true);
            } else {
              handleGoBack();
            }
          }}
          testID="booking-header-close-btn"
          leftIcon={<CloseIcon />}
        />
        <Box justifyContent="center" alignItems="center">
          <Text color="text-primary" variant="mainBaseSemiBold">
            {title ? title : t`Booking Details`}
          </Text>
          <Text color="text-secondary" variant="mainTiny">
            {club?.name}
          </Text>
        </Box>
        <ClubLogo clubRef={club ?? null} />
      </HStack>
      <PopUpModal
        isOpen={confirmationModalOpen}
        upperTitle={t`Leave Booking`}
        theme="danger"
        cancelText={t`Cancel`}
        confirmText={t`Leave`}
        isConfirmDisabled={isLeaveButtonDisabled}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleUnlockSlot();
          } else {
            setIsLeaveButtonDisabled(false);
            setConfirmationModalOpen(false);
          }
        }}
        content={
          <VStack>
            <Text color="text-secondary">
              {t`Are you sure you want to leave this booking? All progress will be lost.`}
            </Text>
          </VStack>
        }
      />
    </>
  );
};

export const BookingHeaderFragmentString = graphql`
  fragment BookingHeaderFragment_BookingSummary on Club {
    name
    ...ClubLogo_Club
  }
`;

export const BookingHeaderFragmentBookingString = graphql`
  fragment BookingHeaderFragment_booking on Booking {
    ... on StandardBooking {
      status
    }
    ... on LessonBooking {
      status
    }
  }
`;
